/**
 * Customer Dashboard - Todo en Local Marketplace
 * Shows order history, tracking, profile and notifications
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { getWsUrl } from '../../config';
import { MarketplaceHeader, MarketplaceFooter, MobileBottomNav } from '../../components/marketplace/MarketplaceLayout';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { 
  Package, 
  Clock, 
  MapPin, 
  Phone, 
  User, 
  Settings, 
  LogOut,
  ChevronRight,
  ChevronLeft,
  Truck,
  CheckCircle,
  XCircle,
  Store,
  Home,
  MessageCircle,
  Bell,
  CreditCard,
  Banknote,
  Search,
  Filter,
  Star,
  Calendar,
  DollarSign,
  ShoppingBag,
  Eye,
  X,
  Send,
  Navigation,
  History,
  Heart,
  Edit3,
  Save,
  Plus,
  Trash2,
  RefreshCw,
  ArrowLeft,
  Loader2,
  Move,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import AffiliateAccountContent from '../pass/AffiliateAccountContent';
import { getErrorMessage } from '../../utils/errorUtils';
import { 
  requestNotificationPermission, 
  notifyDriverNearby, 
  notifyOrderDelivered, 
  notifyNewMessage,
  playNotificationSound 
} from '../../utils/notifications';
import 'leaflet/dist/leaflet.css';

// Fix leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom SVG icon for delivery driver (motorcycle)
const motorcycleIconSvg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="48" height="48">
  <circle cx="32" cy="32" r="30" fill="#3B82F6" stroke="#1E40AF" stroke-width="2"/>
  <path d="M20 38c0-3.3 2.7-6 6-6s6 2.7 6 6-2.7 6-6 6-6-2.7-6-6zm18 0c0-3.3 2.7-6 6-6s6 2.7 6 6-2.7 6-6 6-6-2.7-6-6z" fill="#1E40AF"/>
  <path d="M26 38c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm18 0c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2z" fill="white"/>
  <path d="M22 32h20l-4-10h-8l-2 4h-4l-2 6z" fill="#1E40AF"/>
  <circle cx="32" cy="24" r="6" fill="#FCD34D"/>
  <circle cx="32" cy="24" r="4" fill="#F59E0B"/>
</svg>
`;

// Custom markers
const driverIcon = L.divIcon({
  html: motorcycleIconSvg,
  className: 'driver-marker',
  iconSize: [48, 48],
  iconAnchor: [24, 24],
  popupAnchor: [0, -24]
});

const destinationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const storeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Address map marker icon
const addressMarkerIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Draggable marker for address map
const AddressDraggableMarker = ({ position, onPositionChange }) => {
  const markerRef = React.useRef(null);
  const eventHandlers = {
    dragend() {
      const marker = markerRef.current;
      if (marker) {
        const newPos = marker.getLatLng();
        onPositionChange({ lat: newPos.lat, lng: newPos.lng });
      }
    },
  };
  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={[position.lat, position.lng]}
      ref={markerRef}
      icon={addressMarkerIcon}
    />
  );
};

// Map click handler for address selection
const AddressMapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => {
      onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
};

// Number formatter for Mexican pesos
const formatMXN = (value) => {
  if (!value && value !== 0) return '$0.00';
  return '$' + Number(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Brand colors
const COLORS = {
  navyBlue: '#1e3a5f',
  darkSlate: '#0f172a',
  orange: '#F37322',
  lightGray: '#f8fafc',
  white: '#ffffff'
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customerToken] = useState(localStorage.getItem('customer_token'));
  const [customer, setCustomer] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersPage, setOrdersPage] = useState(1);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'orders');
  const [statusFilter, setStatusFilter] = useState('all');

  // Profile editing state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ full_name: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  // Address state
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState({ label: 'Casa', address: '', references: '' });
  const [addressCoords, setAddressCoords] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  
  // Tracking state
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showChatModal, setShowChatModal] = useState(false);
  
  // Rating state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingOrder, setRatingOrder] = useState(null);
  const [storeRating, setStoreRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  
  // WebSocket ref for real-time tracking
  const wsRef = React.useRef(null);
  const trackingIntervalRef = React.useRef(null);

  // Ref for chat auto-scroll
  const chatMessagesRef = React.useRef(null);
  
  // Predefined messages for customer
  const predefinedMessages = [
    "Ya salgo",
    "Espérame un momento",
    "Estoy en el portón",
    "Puedes dejar el pedido con el vigilante",
    "¿Cuánto tiempo tardas?"
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!customerToken) {
      navigate('/auth?redirect=/mi-cuenta');
      return;
    }
    loadData();
  }, [customerToken, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profileRes, ordersRes] = await Promise.all([
        api.get(`/marketplace/customer/profile?customer_token=${customerToken}`),
        api.get(`/marketplace/orders?customer_token=${customerToken}&page=1&limit=10`)
      ]);

      setCustomer(profileRes.data.customer);
      setStatistics(profileRes.data.statistics);
      setOrders(ordersRes.data.orders || []);
      setHasMoreOrders(ordersRes.data.has_more || false);
      setOrdersPage(1);
      setProfileForm({
        full_name: profileRes.data.customer?.full_name || '',
        phone: profileRes.data.customer?.phone || ''
      });
      setAddresses(profileRes.data.saved_addresses || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('customer_token');
        localStorage.removeItem('customer_data');
        navigate('/auth?redirect=/mi-cuenta');
      } else {
        toast.error(getErrorMessage(error, 'Error al cargar datos'));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    try {
      setLoadingMore(true);
      const nextPage = ordersPage + 1;
      const res = await api.get(`/marketplace/orders?customer_token=${customerToken}&page=${nextPage}&limit=10`);
      setOrders(prev => [...prev, ...(res.data.orders || [])]);
      setHasMoreOrders(res.data.has_more || false);
      setOrdersPage(nextPage);
    } catch (error) {
      toast.error('Error al cargar más pedidos');
    } finally {
      setLoadingMore(false);
    }
  };

  const loadOrderTracking = async (order) => {
    try {
      const res = await api.get(`/marketplace/orders/${order.id}/tracking?customer_token=${customerToken}`);
      setTrackingData(res.data);
      setSelectedOrder(order);
      
      // Load chat messages
      try {
        const chatRes = await api.get(`/marketplace/orders/${order.id}/chat?customer_token=${customerToken}`);
        setChatMessages(Array.isArray(chatRes.data) ? chatRes.data : chatRes.data?.messages || []);
      } catch (e) {
        setChatMessages([]);
      }
      
      // Start real-time tracking via WebSocket for in_transit/nearby orders
      if (['in_transit', 'nearby'].includes(order.status) || ['in_transit', 'nearby'].includes(res.data.status)) {
        startWebSocketTracking(order.id);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar tracking'));
    }
  };
  
  // WebSocket for real-time tracking
  const startWebSocketTracking = (orderId) => {
    // Stop any existing connection
    stopTrackingUpdates();

    // Build WebSocket URL
    const wsUrl = `${getWsUrl()}/api/v1/delivery/ws/track/${orderId}`;
    
    console.log('Connecting to WebSocket:', wsUrl);
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('WebSocket connected for tracking');
        // Send ping to keep connection alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 30000);
        ws.pingInterval = pingInterval;
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message:', data.type);
          
          if (data.type === 'location_update') {
            // Update driver location in tracking data
            setTrackingData(prev => ({
              ...prev,
              driver: {
                ...prev?.driver,
                location: data.driver_location
              },
              eta_minutes: data.eta_minutes || prev?.eta_minutes
            }));
          } else if (data.type === 'status_update') {
            // Update order status
            setTrackingData(prev => ({
              ...prev,
              status: data.status,
              driver: data.driver || prev?.driver
            }));
            
            // Show notification with sound
            if (data.status === 'nearby') {
              toast.info('🏍️ ¡Tu repartidor está cerca!');
              notifyDriverNearby(data.driver?.name || 'Tu repartidor', data.eta_minutes || 5);
            } else if (data.status === 'delivered') {
              toast.success('✅ ¡Tu pedido ha sido entregado!');
              notifyOrderDelivered(trackingData?.order_number || '');
              stopTrackingUpdates();
            }
          } else if (data.type === 'chat_message') {
            // New chat message from driver
            setChatMessages(prev => [...prev, data.message]);
            toast.info('💬 Nuevo mensaje del repartidor');
            notifyNewMessage(data.message?.sender_name || 'Repartidor', data.message?.message || '');
          }
        } catch (e) {
          // Might be a pong response
          if (event.data !== 'pong') {
            console.error('Error parsing WebSocket message:', e);
          }
        }
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        // Fallback to polling if WebSocket fails
        startPollingFallback(orderId);
      };
      
      ws.onclose = () => {
        console.log('WebSocket closed');
        if (ws.pingInterval) clearInterval(ws.pingInterval);
      };
      
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      // Fallback to polling
      startPollingFallback(orderId);
    }
  };
  
  // Fallback to polling if WebSocket not available
  const startPollingFallback = (orderId) => {
    if (trackingIntervalRef.current) return; // Already polling
    
    console.log('Falling back to polling for tracking');
    trackingIntervalRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/marketplace/orders/${orderId}/tracking?customer_token=${customerToken}`);
        setTrackingData(res.data);
        
        // Also refresh chat messages
        try {
          const chatRes = await api.get(`/marketplace/orders/${orderId}/chat?customer_token=${customerToken}`);
          setChatMessages(Array.isArray(chatRes.data) ? chatRes.data : chatRes.data?.messages || []);
        } catch (chatErr) {
          // Silently fail - chat is optional
        }
        
        if (['delivered', 'completed', 'cancelled'].includes(res.data.status)) {
          stopTrackingUpdates();
          toast.success('¡Tu pedido ha sido entregado!');
        }
      } catch (error) {
        console.error('Error polling tracking:', error);
      }
    }, 10000);
  };
  
  const stopTrackingUpdates = () => {
    // Close WebSocket if open
    if (wsRef.current) {
      if (wsRef.current.pingInterval) {
        clearInterval(wsRef.current.pingInterval);
      }
      if (wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }
    // Clear polling interval
    if (trackingIntervalRef.current) {
      clearInterval(trackingIntervalRef.current);
      trackingIntervalRef.current = null;
    }
  };
  
  // Cleanup on unmount or when order changes
  React.useEffect(() => {
    return () => stopTrackingUpdates();
  }, [selectedOrder]);

  // Auto-scroll chat to bottom when new messages arrive
  React.useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);
  
  // Stop tracking when going back
  const handleBackFromTracking = () => {
    stopTrackingUpdates();
    setSelectedOrder(null);
    setTrackingData(null);
  };

  const sendChatMessage = async (message, isPredefined = false) => {
    if (!selectedOrder) return;
    
    try {
      const res = await api.post(`/marketplace/orders/${selectedOrder.id}/chat?customer_token=${customerToken}`, {
        message,
        is_predefined: isPredefined
      });
      
      setChatMessages(prev => [...prev, res.data]);
      setNewMessage('');
      toast.success('Mensaje enviado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al enviar mensaje'));
    }
  };

  // Rating functions
  const openRatingModal = (order) => {
    setRatingOrder(order);
    setStoreRating(0);
    setRatingComment('');
    setShowRatingModal(true);
  };

  const submitRating = async () => {
    if (!ratingOrder || storeRating === 0) {
      toast.error('Por favor selecciona una calificación');
      return;
    }

    try {
      setSubmittingRating(true);
      
      // Submit store review
      await api.post(`/marketplace/reviews/store?customer_token=${customerToken}`, {
        business_id: ratingOrder.business_id,
        order_id: ratingOrder.id,
        rating: storeRating,
        comment: ratingComment
      });

      // Mark order as rated locally
      setOrders(prev => prev.map(o => 
        o.id === ratingOrder.id ? { ...o, rated: true } : o
      ));

      toast.success('¡Gracias por tu calificación!');
      setShowRatingModal(false);
      setRatingOrder(null);
      setStoreRating(0);
      setRatingComment('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al enviar calificación'));
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
    navigate('/');
    toast.success('Sesión cerrada');
  };

  // Save profile
  const saveProfile = async () => {
    if (!profileForm.full_name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    try {
      setSavingProfile(true);
      await api.put(`/marketplace/customer/profile?customer_token=${customerToken}`, profileForm);
      setCustomer(prev => ({ ...prev, ...profileForm }));
      // Update localStorage too
      const stored = JSON.parse(localStorage.getItem('customer_data') || '{}');
      localStorage.setItem('customer_data', JSON.stringify({ ...stored, full_name: profileForm.full_name, phone: profileForm.phone }));
      setEditingProfile(false);
      toast.success('Perfil actualizado');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar'));
    } finally {
      setSavingProfile(false);
    }
  };

  // Reverse geocode coordinates to address
  const reverseGeocode = async (coords) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lng}&addressdetails=1`
      );
      const data = await response.json();
      if (data.address) {
        const parts = [];
        if (data.address.road) parts.push(data.address.road);
        if (data.address.house_number) parts.push(data.address.house_number);
        if (data.address.suburb || data.address.neighbourhood) parts.push(data.address.suburb || data.address.neighbourhood);
        if (data.address.city || data.address.town) parts.push(data.address.city || data.address.town);
        return parts.join(', ');
      }
      return null;
    } catch {
      return null;
    }
  };

  // Handle map position change (click or drag)
  const handleAddressMapPosition = async (coords) => {
    setAddressCoords(coords);
    const address = await reverseGeocode(coords);
    if (address) {
      setAddressForm(f => ({ ...f, address }));
    }
  };

  // Use current location for address
  const useCurrentLocationForAddress = () => {
    if (!navigator.geolocation) {
      toast.error('Tu navegador no soporta geolocalización');
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const coords = { lat: position.coords.latitude, lng: position.coords.longitude };
        setAddressCoords(coords);
        const address = await reverseGeocode(coords);
        if (address) {
          setAddressForm(f => ({ ...f, address }));
        }
        setGettingLocation(false);
        toast.success('Ubicación obtenida');
      },
      (error) => {
        setGettingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error('Permiso de ubicación denegado');
        } else {
          toast.error('Error al obtener ubicación');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Add address
  const addAddress = async () => {
    if (!addressForm.address.trim()) {
      toast.error('La dirección es obligatoria');
      return;
    }
    try {
      setSavingAddress(true);
      const res = await api.post(`/marketplace/customer/addresses?customer_token=${customerToken}`, {
        ...addressForm,
        location: addressCoords || null,
        is_default: addresses.length === 0
      });
      setAddresses(prev => [...prev, res.data]);
      setAddressForm({ label: 'Casa', address: '', references: '' });
      setAddressCoords(null);
      setShowAddressForm(false);
      toast.success('Dirección guardada');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar'));
    } finally {
      setSavingAddress(false);
    }
  };

  // Delete address
  const deleteAddress = async (addressId) => {
    try {
      await api.delete(`/marketplace/customer/addresses/${addressId}?customer_token=${customerToken}`);
      setAddresses(prev => prev.filter(a => a.id !== addressId));
      toast.success('Dirección eliminada');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar'));
    }
  };

  // Reorder
  const handleReorder = (order) => {
    // Navigate to the store page to re-add items
    if (order.business_id) {
      navigate(`/tienda/${order.business_id}`);
    } else {
      navigate('/buscar');
    }
  };

  // Tab change with URL sync
  const changeTab = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Request notification permission on mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-700', label: 'Pendiente', icon: Clock },
      new: { color: 'bg-yellow-100 text-yellow-700', label: 'Nuevo', icon: Clock },
      confirmed: { color: 'bg-blue-100 text-blue-700', label: 'Confirmado', icon: CheckCircle },
      preparing: { color: 'bg-purple-100 text-purple-700', label: 'En preparación', icon: Package },
      ready: { color: 'bg-green-100 text-green-700', label: 'Listo', icon: CheckCircle },
      ready_for_pickup: { color: 'bg-green-100 text-green-700', label: 'Listo para recoger', icon: Store },
      waiting_driver: { color: 'bg-orange-100 text-orange-700', label: 'Esperando repartidor', icon: Truck },
      in_transit: { color: 'bg-blue-100 text-blue-700', label: 'En camino', icon: Truck },
      nearby: { color: 'bg-orange-100 text-orange-700', label: 'Cerca de ti', icon: MapPin },
      delivered: { color: 'bg-green-100 text-green-700', label: 'Entregado', icon: CheckCircle },
      completed: { color: 'bg-green-100 text-green-700', label: 'Completado', icon: CheckCircle },
      cancelled: { color: 'bg-red-100 text-red-700', label: 'Cancelado', icon: XCircle },
      // Super Inteligente statuses
      stores_notified: { color: 'bg-yellow-100 text-yellow-700', label: 'Tiendas notificadas', icon: Clock },
      driver_assigned: { color: 'bg-blue-100 text-blue-700', label: 'Repartidor asignado', icon: Truck },
      picking_up: { color: 'bg-purple-100 text-purple-700', label: 'Recogiendo', icon: Package },
      delivering: { color: 'bg-blue-100 text-blue-700', label: 'En camino', icon: Truck },
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const filteredOrders = orders.filter(order => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return ['pending', 'new', 'confirmed', 'preparing', 'ready', 'ready_for_pickup', 'waiting_driver', 'in_transit', 'nearby', 'stores_notified', 'driver_assigned', 'picking_up', 'delivering'].includes(order.status);
    if (statusFilter === 'completed') return ['delivered', 'completed'].includes(order.status);
    return order.status === statusFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <MarketplaceHeader />
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
          {/* Profile skeleton */}
          <div className="bg-slate-800 rounded-2xl p-6 animate-pulse">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full" />
              <div className="space-y-2 flex-1">
                <div className="h-5 bg-white/20 rounded w-40" />
                <div className="h-4 bg-white/10 rounded w-56" />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
              {[1,2,3,4].map(i => <div key={i} className="h-16 bg-white/10 rounded-xl" />)}
            </div>
          </div>
          {/* Tabs skeleton */}
          <div className="bg-white rounded-xl h-12 animate-pulse" />
          {/* Orders skeleton */}
          {[1,2,3].map(i => (
            <div key={i} className="bg-white rounded-xl p-4 animate-pulse space-y-3">
              <div className="flex justify-between">
                <div className="h-4 bg-slate-200 rounded w-28" />
                <div className="h-6 bg-slate-200 rounded-full w-20" />
              </div>
              <div className="h-3 bg-slate-200 rounded w-3/4" />
              <div className="h-3 bg-slate-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <MarketplaceHeader />

      <main className="max-w-5xl mx-auto px-4 py-6 flex-1 w-full pb-20 sm:pb-6">
        {/* Customer Info Header */}
        <div className="rounded-2xl p-6 mb-6 text-white" style={{ background: `linear-gradient(135deg, ${COLORS.navyBlue}, ${COLORS.darkSlate})` }}>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-8 h-8" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold truncate">{customer?.full_name}</h1>
              <p className="text-white/70 text-sm truncate">{customer?.email}</p>
            </div>
            {/* Quick actions */}
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/favoritos" className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors" title="Favoritos">
                <Heart className="w-5 h-5" />
              </Link>
              <button onClick={handleLogout} className="p-2 bg-white/10 rounded-full hover:bg-red-500/30 transition-colors" title="Cerrar sesión">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{statistics?.total_orders || 0}</p>
              <p className="text-xs text-white/70">Pedidos</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{formatMXN(statistics?.total_spent)}</p>
              <p className="text-xs text-white/70">Total gastado</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold">{statistics?.completed_orders || 0}</p>
              <p className="text-xs text-white/70">Completados</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold" style={{ color: COLORS.orange }}>{statistics?.pending_orders || 0}</p>
              <p className="text-xs text-white/70">En proceso</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6 overflow-x-auto">
          <div className="flex border-b">
            {[
              { id: 'orders', label: 'Pedidos', icon: ShoppingBag },
              { id: 'history', label: 'Historial', icon: History },
              { id: 'tracking', label: 'Tracking', icon: MapPin },
              { id: 'profile', label: 'Perfil', icon: User },
              { id: 'addresses', label: 'Direcciones', icon: Home },
              { id: 'affiliate', label: 'Mi Cuenta Afiliados', icon: Sparkles }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => changeTab(tab.id)}
                className={`flex-1 min-w-[80px] flex items-center justify-center gap-1.5 px-3 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-b-2 bg-orange-50'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                style={activeTab === tab.id ? { color: COLORS.orange, borderColor: COLORS.orange } : {}}
              >
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {/* Filter */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {[
                { value: 'all', label: 'Todos' },
                { value: 'active', label: 'Activos' },
                { value: 'completed', label: 'Completados' },
                { value: 'cancelled', label: 'Cancelados' }
              ].map(filter => (
                <button
                  key={filter.value}
                  onClick={() => setStatusFilter(filter.value)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    statusFilter === filter.value
                      ? 'text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-100'
                  }`}
                  style={statusFilter === filter.value ? { backgroundColor: COLORS.navyBlue } : {}}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            {/* Orders List */}
            {filteredOrders.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No tienes pedidos</h3>
                <p className="text-slate-500 mb-4">¡Empieza a comprar en Todo en Local!</p>
                <Link
                  to="/buscar"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-white rounded-lg transition-colors font-medium"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  <Search className="w-4 h-4" />
                  Explorar productos
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map(order => (
                  <div 
                    key={order.id} 
                    className="bg-white rounded-xl shadow-sm overflow-hidden"
                    data-testid={`order-card-${order.id}`}
                  >
                    {/* Order Header */}
                    <div className="p-4 border-b border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-semibold text-slate-900">#{order.order_number}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(order.created_at).toLocaleDateString('es-MX', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        {getStatusBadge(order.status)}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Store className="w-4 h-4" />
                        <span>{order.business_name}</span>
                      </div>
                    </div>

                    {/* Order Items */}
                    <div className="p-4 bg-slate-50">
                      <div className="space-y-2">
                        {order.items?.slice(0, 3).map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm">
                            <span className="text-slate-700">
                              {item.quantity}x {item.product_name}
                            </span>
                            <span className="text-slate-900 font-medium">
                              ${item.subtotal?.toFixed(2)}
                            </span>
                          </div>
                        ))}
                        {order.items?.length > 3 && (
                          <p className="text-sm text-slate-500">+{order.items.length - 3} productos más</p>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
                        <span className="font-medium text-slate-900">Total</span>
                        <span className="font-bold text-lg text-slate-900">${order.total?.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Order Actions */}
                    <div className="p-4 flex items-center gap-3">
                      <div className="flex-1 flex items-center gap-2 text-sm text-slate-500">
                        {order.delivery_type === 'delivery' ? (
                          <>
                            <Truck className="w-4 h-4" />
                            <span>Envío a domicilio</span>
                          </>
                        ) : order.delivery_type === 'en_local' ? (
                          <>
                            <Truck className="w-4 h-4 text-emerald-600" />
                            <span>Envío por enLocal</span>
                          </>
                        ) : (
                          <>
                            <Store className="w-4 h-4" />
                            <span>Recoger en tienda</span>
                          </>
                        )}
                      </div>
                      {['in_transit', 'nearby', 'pending', 'new', 'confirmed', 'preparing', 'ready', 'ready_for_pickup', 'waiting_driver', 'stores_notified', 'driver_assigned', 'picking_up', 'delivering'].includes(order.status) && (
                        <button
                          onClick={() => {
                            loadOrderTracking(order);
                            setActiveTab('tracking');
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          Ver seguimiento
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Load more button */}
            {hasMoreOrders && (
              <button
                onClick={loadMoreOrders}
                disabled={loadingMore}
                className="w-full py-3 bg-white rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors border flex items-center justify-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                    Cargando...
                  </>
                ) : (
                  'Cargar más pedidos'
                )}
              </button>
            )}
          </div>
        )}

        {/* History Tab - Completed/Delivered Orders */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <History className="w-5 h-5" />
              Historial de Entregas
            </h2>
            {orders.filter(o => ['delivered', 'completed'].includes(o.status)).length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Sin entregas completadas</h3>
                <p className="text-slate-500">Aquí aparecerán tus pedidos entregados</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.filter(o => ['delivered', 'completed'].includes(o.status)).map(order => (
                  <div 
                    key={order.id} 
                    className="bg-white rounded-xl shadow-sm overflow-hidden"
                    data-testid={`history-order-${order.id}`}
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-semibold text-slate-900">#{order.order_number}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(order.created_at).toLocaleDateString('es-MX', { 
                              day: 'numeric', 
                              month: 'long', 
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        {getStatusBadge(order.status)}
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                        <Store className="w-4 h-4 text-slate-400" />
                        <span>{order.business_name || 'Tienda'}</span>
                      </div>
                      
                      {/* Detailed Order Items - Full breakdown */}
                      <div className="bg-slate-50 rounded-lg p-3 mb-3">
                        <p className="text-xs font-medium text-slate-500 mb-2 uppercase tracking-wide">Desglose del pedido</p>
                        <div className="space-y-2">
                          {(order.items || []).map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm">
                              <div className="flex-1">
                                <span className="text-slate-900 font-medium">{item.quantity}x </span>
                                <span className="text-slate-700">{item.product_name || item.name}</span>
                              </div>
                              <div className="text-right">
                                <span className="text-slate-500 text-xs mr-2">
                                  ${(item.unit_price || item.price || 0).toFixed(2)} c/u
                                </span>
                                <span className="text-slate-900 font-medium">
                                  ${(item.subtotal || (item.unit_price || item.price || 0) * item.quantity).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Subtotals and Total */}
                        <div className="border-t border-slate-200 mt-3 pt-3 space-y-1">
                          <div className="flex justify-between text-sm text-slate-600">
                            <span>Subtotal</span>
                            <span>${(order.subtotal || order.total || 0).toFixed(2)}</span>
                          </div>
                          {order.delivery_fee > 0 && (
                            <div className="flex justify-between text-sm text-slate-600">
                              <span>Envío</span>
                              <span>${order.delivery_fee.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-base font-bold text-slate-900 pt-1">
                            <span>Total</span>
                            <span style={{ color: COLORS.navyBlue }}>${(order.total || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions: Rate + Reorder */}
                      <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                        {!order.rated ? (
                          <button
                            onClick={() => openRatingModal(order)}
                            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-yellow-50 text-yellow-700 rounded-lg text-sm font-medium hover:bg-yellow-100 transition-colors"
                            data-testid={`rate-order-btn-${order.id}`}
                          >
                            <Star className="w-4 h-4" />
                            Calificar
                          </button>
                        ) : (
                          <div className="flex-1 flex items-center justify-center gap-2 text-sm text-emerald-600">
                            <CheckCircle className="w-4 h-4" />
                            Calificado
                          </div>
                        )}
                        <button
                          onClick={() => handleReorder(order)}
                          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium text-white transition-colors"
                          style={{ backgroundColor: COLORS.orange }}
                        >
                          <RefreshCw className="w-4 h-4" />
                          Pedir de nuevo
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Load more button - History */}
            {hasMoreOrders && (
              <button
                onClick={loadMoreOrders}
                disabled={loadingMore}
                className="w-full py-3 bg-white rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors border flex items-center justify-center gap-2"
              >
                {loadingMore ? (
                  <>
                    <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                    Cargando...
                  </>
                ) : (
                  'Cargar más pedidos'
                )}
              </button>
            )}
          </div>
        )}

        {/* Tracking Tab */}
        {activeTab === 'tracking' && (
          <div className="space-y-4">
            {!selectedOrder ? (
              <>
                <h2 className="text-lg font-semibold text-slate-900">Pedidos en proceso</h2>
                {orders.filter(o => ['pending', 'new', 'confirmed', 'preparing', 'ready', 'ready_for_pickup', 'waiting_driver', 'in_transit', 'nearby', 'stores_notified', 'driver_assigned', 'picking_up', 'delivering'].includes(o.status)).length === 0 ? (
                  <div className="bg-white rounded-xl p-12 text-center">
                    <Truck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">Sin pedidos activos</h3>
                    <p className="text-slate-500">No tienes pedidos en proceso de entrega</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {orders.filter(o => ['pending', 'new', 'confirmed', 'preparing', 'ready', 'ready_for_pickup', 'waiting_driver', 'in_transit', 'nearby', 'stores_notified', 'driver_assigned', 'picking_up', 'delivering'].includes(o.status)).map(order => (
                      <button
                        key={order.id}
                        onClick={() => loadOrderTracking(order)}
                        className="w-full bg-white rounded-xl p-4 text-left hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-slate-900">#{order.order_number}</p>
                            <p className="text-sm text-slate-500">{order.business_name}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(order.status)}
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Back button */}
                <button
                  onClick={handleBackFromTracking}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Volver
                </button>

                {/* Order info with ETA */}
                <div className="bg-white rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="font-semibold text-slate-900">#{trackingData?.order_number}</h2>
                    {getStatusBadge(trackingData?.status)}
                  </div>
                  
                  {/* ETA dinámico prominente */}
                  {trackingData?.eta_minutes && ['in_transit', 'nearby', 'waiting_driver'].includes(trackingData?.status) && (
                    <div className="mt-3 p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          <Clock className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-sm text-orange-700">Tiempo estimado de llegada</p>
                          <p className="text-2xl font-bold text-orange-600">{trackingData.eta_minutes} minutos</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Real-time indicator */}
                  {['in_transit', 'nearby'].includes(trackingData?.status) && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                      Actualizando en tiempo real
                    </div>
                  )}
                </div>

                {/* Map for delivery tracking - Show when status is in_transit or nearby */}
                {(trackingData?.delivery_type === 'delivery' || trackingData?.delivery_type === 'en_local') && ['in_transit', 'nearby'].includes(trackingData?.status) && (
                  <div className="bg-white rounded-xl overflow-hidden mb-4">
                    <div className="h-72">
                      <MapContainer
                        center={trackingData.driver?.location 
                          ? [trackingData.driver.location.lat, trackingData.driver.location.lng]
                          : trackingData.delivery_location
                          ? [trackingData.delivery_location.lat, trackingData.delivery_location.lng]
                          : [20.659698, -103.349609]
                        }
                        zoom={15}
                        style={{ height: '100%', width: '100%' }}
                      >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        
                        {/* Driver marker - motorcycle icon */}
                        {trackingData.driver?.location && (
                          <Marker 
                            position={[trackingData.driver.location.lat, trackingData.driver.location.lng]}
                            icon={driverIcon}
                          >
                            <Popup>
                              <b>{trackingData.driver.name}</b><br />
                              Tu repartidor
                            </Popup>
                          </Marker>
                        )}
                        
                        {/* Destination marker */}
                        {trackingData.delivery_location && (
                          <Marker 
                            position={[trackingData.delivery_location.lat, trackingData.delivery_location.lng]}
                            icon={destinationIcon}
                          >
                            <Popup>Tu ubicación de entrega</Popup>
                          </Marker>
                        )}
                      </MapContainer>
                    </div>
                    
                    {/* Driver info */}
                    {trackingData.driver && (
                      <div className="p-4 border-t flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                            <Truck className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{trackingData.driver.name}</p>
                            <p className="text-sm text-slate-500">Tu repartidor</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <a
                            href={`tel:${trackingData.driver.phone}`}
                            className="p-3 bg-green-100 text-green-600 rounded-full hover:bg-green-200 transition-colors"
                          >
                            <Phone className="w-5 h-5" />
                          </a>
                          <button
                            onClick={() => setShowChatModal(true)}
                            className="p-3 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 transition-colors"
                          >
                            <MessageCircle className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}
                    
                    {/* No driver location message */}
                    {!trackingData.driver?.location && (
                      <div className="p-4 border-t text-center text-slate-500">
                        <Navigation className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                        <p className="text-sm">Esperando ubicación del repartidor...</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Pickup info */}
                {trackingData?.delivery_type === 'pickup' && trackingData?.branch && (
                  <div className="bg-white rounded-xl p-4 mb-4">
                    <h3 className="font-medium text-slate-900 mb-3">Recoge tu pedido en:</h3>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Store className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{trackingData.branch.name}</p>
                        <p className="text-sm text-slate-500">{trackingData.branch.address}</p>
                        {trackingData.branch.phone && (
                          <a href={`tel:${trackingData.branch.phone}`} className="text-sm text-blue-600 flex items-center gap-1 mt-1">
                            <Phone className="w-3 h-3" />
                            {trackingData.branch.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Timeline */}
                <div className="bg-white rounded-xl p-4">
                  <h3 className="font-medium text-slate-900 mb-4">Estado del pedido</h3>
                  <div className="space-y-4">
                    {trackingData?.timeline?.map((step, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          step.completed ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-400'
                        }`}>
                          {step.completed ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : (
                            <div className="w-3 h-3 rounded-full bg-current" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${step.completed ? 'text-slate-900' : 'text-slate-400'}`}>
                            {step.label}
                          </p>
                          {step.timestamp && (
                            <p className="text-sm text-slate-500">
                              {new Date(step.timestamp).toLocaleTimeString('es-MX', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900">Información personal</h3>
                {!editingProfile ? (
                  <button
                    onClick={() => setEditingProfile(true)}
                    className="flex items-center gap-1.5 text-sm font-medium transition-colors"
                    style={{ color: COLORS.orange }}
                  >
                    <Edit3 className="w-4 h-4" />
                    Editar
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setEditingProfile(false); setProfileForm({ full_name: customer?.full_name || '', phone: customer?.phone || '' }); }}
                      className="px-3 py-1.5 text-sm text-slate-600 border rounded-lg hover:bg-slate-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={saveProfile}
                      disabled={savingProfile}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50"
                      style={{ backgroundColor: COLORS.orange }}
                    >
                      <Save className="w-3.5 h-3.5" />
                      {savingProfile ? 'Guardando...' : 'Guardar'}
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-500 mb-1">Nombre completo</label>
                  {editingProfile ? (
                    <input
                      type="text"
                      value={profileForm.full_name}
                      onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{customer?.full_name}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-slate-500 mb-1">Correo electrónico</label>
                  <p className="font-medium text-slate-900">{customer?.email}</p>
                  {editingProfile && <p className="text-xs text-slate-400 mt-1">El correo no se puede cambiar</p>}
                </div>
                <div>
                  <label className="block text-sm text-slate-500 mb-1">Teléfono</label>
                  {editingProfile ? (
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      placeholder="Ej: 33 1234 5678"
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{customer?.phone || <span className="text-slate-400 italic">No registrado</span>}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-slate-500 mb-1">Miembro desde</label>
                  <p className="font-medium text-slate-900">
                    {customer?.created_at ? new Date(customer.created_at).toLocaleDateString('es-MX', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    }) : '-'}
                  </p>
                </div>
              </div>
            </div>

            {/* Quick links */}
            <div className="bg-white rounded-xl overflow-hidden">
              <Link to="/favoritos" className="flex items-center gap-3 px-6 py-4 hover:bg-slate-50 transition-colors border-b">
                <Heart className="w-5 h-5 text-red-400" />
                <span className="text-slate-700 font-medium">Mis Favoritos</span>
                <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
              </Link>
              <Link to="/carrito" className="flex items-center gap-3 px-6 py-4 hover:bg-slate-50 transition-colors border-b">
                <ShoppingBag className="w-5 h-5 text-orange-400" />
                <span className="text-slate-700 font-medium">Mi Carrito</span>
                <ChevronRight className="w-4 h-4 text-slate-400 ml-auto" />
              </Link>
              <button onClick={handleLogout} className="w-full flex items-center gap-3 px-6 py-4 hover:bg-red-50 transition-colors text-red-600">
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Cerrar sesión</span>
              </button>
            </div>
          </div>
        )}

        {/* Addresses Tab */}
        {activeTab === 'addresses' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Mis Direcciones
              </h2>
              <button
                onClick={() => setShowAddressForm(!showAddressForm)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-white rounded-lg transition-colors"
                style={{ backgroundColor: COLORS.orange }}
              >
                <Plus className="w-4 h-4" />
                Agregar
              </button>
            </div>

            {/* Add address form */}
            {showAddressForm && (
              <div className="bg-white rounded-xl p-6 border-2 border-dashed border-orange-200">
                <h3 className="font-medium text-slate-900 mb-4">Nueva dirección</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-500 mb-1">Etiqueta</label>
                    <select
                      value={addressForm.label}
                      onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                    >
                      <option value="Casa">Casa</option>
                      <option value="Oficina">Oficina</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>

                  {/* Location Map */}
                  <div>
                    <label className="block text-sm text-slate-500 mb-2">Ubicación en el mapa</label>
                    {!addressCoords ? (
                      <button
                        type="button"
                        onClick={useCurrentLocationForAddress}
                        disabled={gettingLocation}
                        className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed border-slate-300 hover:border-blue-400 hover:bg-blue-50 transition-colors text-slate-600 hover:text-blue-600"
                      >
                        {gettingLocation ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Obteniendo ubicación...</span>
                          </>
                        ) : (
                          <>
                            <Navigation className="w-5 h-5" />
                            <span>Usar mi ubicación actual</span>
                          </>
                        )}
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 text-emerald-700 text-sm">
                            <MapPin className="w-4 h-4" />
                            <span>Ubicación seleccionada</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setAddressCoords(null)}
                            className="text-xs text-slate-500 hover:text-red-500"
                          >
                            Cambiar
                          </button>
                        </div>
                        <div className="relative rounded-lg overflow-hidden border-2 border-slate-200">
                          <MapContainer
                            center={[addressCoords.lat, addressCoords.lng]}
                            zoom={16}
                            style={{ height: '220px', width: '100%' }}
                            scrollWheelZoom={true}
                          >
                            <TileLayer
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <AddressDraggableMarker
                              position={addressCoords}
                              onPositionChange={handleAddressMapPosition}
                            />
                            <AddressMapClickHandler onMapClick={handleAddressMapPosition} />
                          </MapContainer>
                          <div className="absolute bottom-2 left-2 right-2 bg-white/90 backdrop-blur-sm rounded-lg p-2 text-xs text-slate-600 flex items-center gap-2 z-[1000]">
                            <Move className="w-4 h-4 text-blue-500 flex-shrink-0" />
                            <span>Arrastra el marcador o toca el mapa para ajustar</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-slate-500 mb-1">Dirección completa</label>
                    <textarea
                      value={addressForm.address}
                      onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })}
                      placeholder={addressCoords ? 'Dirección detectada automáticamente' : 'Calle, número, colonia, ciudad...'}
                      rows={2}
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400 resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-500 mb-1">Referencias (opcional)</label>
                    <input
                      type="text"
                      value={addressForm.references}
                      onChange={(e) => setAddressForm({ ...addressForm, references: e.target.value })}
                      placeholder="Entre calles, color de fachada..."
                      className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-400"
                    />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => { setShowAddressForm(false); setAddressForm({ label: 'Casa', address: '', references: '' }); setAddressCoords(null); }}
                      className="flex-1 py-2.5 border rounded-lg text-slate-600 hover:bg-slate-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={addAddress}
                      disabled={savingAddress}
                      className="flex-1 py-2.5 text-white rounded-lg disabled:opacity-50 font-medium"
                      style={{ backgroundColor: COLORS.navyBlue }}
                    >
                      {savingAddress ? 'Guardando...' : 'Guardar dirección'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Address list */}
            {addresses.length === 0 && !showAddressForm ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Sin direcciones guardadas</h3>
                <p className="text-slate-500 mb-4">Guarda tus direcciones para agilizar tus pedidos</p>
              </div>
            ) : (
              <div className="space-y-3">
                {addresses.map(addr => (
                  <div key={addr.id} className="bg-white rounded-xl p-4 flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${COLORS.navyBlue}15` }}>
                      {addr.label === 'Casa' ? <Home className="w-5 h-5" style={{ color: COLORS.navyBlue }} /> :
                       addr.label === 'Oficina' ? <CreditCard className="w-5 h-5" style={{ color: COLORS.navyBlue }} /> :
                       <MapPin className="w-5 h-5" style={{ color: COLORS.navyBlue }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-900">{addr.label}</p>
                        {addr.is_default && (
                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full text-white" style={{ backgroundColor: COLORS.orange }}>
                            Predeterminada
                          </span>
                        )}
                        {addr.location && (
                          <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700">
                            Con GPS
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5">{addr.address}</p>
                      {addr.references && <p className="text-xs text-slate-400 mt-0.5">Ref: {addr.references}</p>}
                    </div>
                    <button
                      onClick={() => deleteAddress(addr.id)}
                      className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Affiliate Tab — Pass module */}
        {activeTab === 'affiliate' && (
          <AffiliateAccountContent />
        )}
      </main>

      {/* Chat Modal - z-index higher than map */}
      {showChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-xl overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between bg-blue-600 text-white">
              <div className="flex items-center gap-3">
                <MessageCircle className="w-5 h-5" />
                <span className="font-medium">Chat con repartidor</span>
              </div>
              <button onClick={() => setShowChatModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Messages */}
            <div ref={chatMessagesRef} className="h-64 overflow-auto p-4 space-y-2 bg-slate-50">
              {chatMessages.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No hay mensajes aún</p>
              ) : (
                chatMessages.map(msg => (
                  <div
                    key={msg.id}
                    className={`max-w-[80%] p-3 rounded-xl ${
                      msg.sender_type === 'customer'
                        ? 'bg-blue-500 text-white ml-auto'
                        : 'bg-white text-slate-900 shadow-sm'
                    }`}
                  >
                    {msg.message}
                  </div>
                ))
              )}
            </div>

            {/* Predefined messages */}
            <div className="p-3 bg-white border-t">
              <p className="text-xs text-slate-500 mb-2">Mensajes rápidos:</p>
              <div className="flex flex-wrap gap-2">
                {predefinedMessages.map((msg, idx) => (
                  <button
                    key={idx}
                    onClick={() => sendChatMessage(msg, true)}
                    className="px-3 py-1 bg-slate-100 text-slate-700 text-sm rounded-full hover:bg-slate-200"
                  >
                    {msg}
                  </button>
                ))}
              </div>
            </div>

            {/* Input */}
            <div className="p-3 border-t flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Escribe un mensaje..."
                className="flex-1 px-4 py-2 border rounded-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => e.key === 'Enter' && newMessage && sendChatMessage(newMessage)}
              />
              <button
                onClick={() => newMessage && sendChatMessage(newMessage)}
                disabled={!newMessage}
                className="p-2 bg-blue-600 text-white rounded-full disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      <MarketplaceFooter />
      <MobileBottomNav />

      {/* Rating Modal */}
      {showRatingModal && ratingOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-white w-full max-w-md rounded-xl overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
              <div className="flex items-center gap-3">
                <Star className="w-5 h-5" />
                <span className="font-medium">Calificar pedido</span>
              </div>
              <button onClick={() => setShowRatingModal(false)} className="hover:bg-white/20 rounded-full p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Order info */}
              <div className="text-center mb-6">
                <p className="text-sm text-slate-500 mb-1">Pedido #{ratingOrder.order_number}</p>
                <p className="font-semibold text-slate-900">{ratingOrder.business_name}</p>
              </div>

              {/* Star rating */}
              <div className="mb-6">
                <p className="text-sm text-slate-600 text-center mb-3">¿Cómo fue tu experiencia?</p>
                <div className="flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      onClick={() => setStoreRating(star)}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`rating-star-${star}`}
                    >
                      <Star 
                        className={`w-10 h-10 ${star <= storeRating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`}
                      />
                    </button>
                  ))}
                </div>
                {storeRating > 0 && (
                  <p className="text-center text-sm text-slate-500 mt-2">
                    {storeRating === 1 && 'Muy malo'}
                    {storeRating === 2 && 'Malo'}
                    {storeRating === 3 && 'Regular'}
                    {storeRating === 4 && 'Bueno'}
                    {storeRating === 5 && '¡Excelente!'}
                  </p>
                )}
              </div>

              {/* Comment */}
              <div className="mb-6">
                <label className="block text-sm text-slate-600 mb-2">Comentario (opcional)</label>
                <textarea
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  placeholder="Cuéntanos más sobre tu experiencia..."
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none"
                  rows={3}
                />
              </div>

              {/* Submit button */}
              <button
                onClick={submitRating}
                disabled={storeRating === 0 || submittingRating}
                className="w-full py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                data-testid="submit-rating-btn"
              >
                {submittingRating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <Star className="w-5 h-5" />
                    Enviar calificación
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDashboard;
